import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BrandXS,
  HeadlineL,
  LabelS,
  LabelXS,
} from '@sorare/core/src/atoms/typography';
import { Ordinal } from '@sorare/core/src/atoms/typography/Ordinal';
import { TrophyIcon } from '@sorare/core/src/components/club/Trophies';
import { useNewLobbyFF } from '@sorare/core/src/hooks/useNewLobbyFF';
import { glossary } from '@sorare/core/src/lib/glossary';
import { WithRewardRange } from '@sorare/core/src/lib/rewards';

import { LineupRewardsDistanceLabel } from 'components/lineup/LineupRewardsDistanceLabel';

import { ColoredRanking } from './ColoredRanking';

const gradientText = css`
  background: linear-gradient(
    323deg,
    var(--from-gradient-color) 14.3%,
    var(--to-gradient-color) 85.7%
  );
  background-size: 100%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  -moz-text-fill-color: transparent;
`;

const StyledBodyM = styled(HeadlineL).attrs({
  as: 'p',
  bold: true,
  brand25: true,
})`
  line-height: 1;
  &.rank-1 {
    --from-gradient-color: #ae5e13;
    --to-gradient-color: #fcff63;
    ${gradientText}
  }
  &.rank-2 {
    --from-gradient-color: #bcc8cb;
    --to-gradient-color: #99bdc8;
    ${gradientText}
  }
  &.rank-3 {
    --from-gradient-color: #cc8645;
    --to-gradient-color: #ffa463;
    ${gradientText}
  }
`;

const StyledSup = styled(LabelXS).attrs({ brand25: true, as: 'sup' })``;

const BrandSup = styled(BrandXS).attrs({ as: 'sup', bold: true })``;

type Props = WithRewardRange<object> & {
  scoreDistanceFromMyRanking?: number;
  useBrandFont?: boolean;
  hideScoreDistance?: boolean;
};

export const RankRewardTitle = ({
  startRank,
  endRank,
  startPct,
  endPct,
  scoreDistanceFromMyRanking,
  hideScoreDistance,
  // TODO: remove when useNewLobby is true for all users
  useBrandFont,
}: Props) => {
  const start = startRank ?? (startPct || 0);
  const end = endRank ?? (endPct || 0);

  const useNewLobby = useNewLobbyFF();

  const RankingWrapper =
    useBrandFont || useNewLobby ? ColoredRanking : StyledBodyM;
  const Sup = useBrandFont || useNewLobby ? BrandSup : StyledSup;

  return (
    <Vertical gap={0.5}>
      {start === end ? (
        <Horizontal>
          <TrophyIcon ranking={start} />
          <RankingWrapper className={`rank-${start}`}>
            <Ordinal ordinal={start} Sup={Sup} />
          </RankingWrapper>
        </Horizontal>
      ) : (
        <Horizontal gap={0.5}>
          <RankingWrapper>
            {startRank ? (
              <Ordinal ordinal={start} Sup={Sup} />
            ) : (
              <FormattedMessage
                {...glossary.percentWithSup}
                values={{ sup: c => <Sup>{c}</Sup>, percent: start * 100 }}
              />
            )}
          </RankingWrapper>
          <RankingWrapper>-</RankingWrapper>
          <RankingWrapper>
            {endRank ? (
              <Ordinal ordinal={end} Sup={Sup} />
            ) : (
              <FormattedMessage
                {...glossary.percentWithSup}
                values={{ sup: c => <Sup>{c}</Sup>, percent: end * 100 }}
              />
            )}
          </RankingWrapper>
        </Horizontal>
      )}
      {!!scoreDistanceFromMyRanking && !hideScoreDistance && (
        <LabelS>
          <LineupRewardsDistanceLabel
            distance={scoreDistanceFromMyRanking}
            withFromAppendix={false}
          />
        </LabelS>
      )}
    </Vertical>
  );
};
